<template>
  <div :class="{'open' : open}" class="dropdown">
    <div :class="{'input-active' : searchText}" class="input-container">
      <div @click="clear" class="close-icon icon" v-if="searchText">
        <img :src="closeIcon">
      </div>
      <input
        :class="{'uk-form-danger': error && submitted, 'input-fields': !stylePlaceholder, 'input-selected': stylePlaceholder}"
        :placeholder="resolvePlaceholder"
        @blur="setOpen(false)"
        @click="setOpen(!open)"
        @focusout="handleFocusOut"
        @input="searchChanged"
        @keydown.down="down"
        @keydown.enter="updateList(matches[highlightIndex][1])"
        @keydown.esc="setOpen(false)"
        @keydown.up="up"
        class="uk-search-input"
        ref="strandsSearch"
        type="search"
        v-model="searchText"
        v-on:keydown.enter.prevent>
      <div v-if="fetchComplete" class="custom-overlay">
        <a @click="setOpen(!open)" @mousedown.prevent class="toggle">
          <div class="uk-search-icon icon"><img :src="dropdownOpen"></div>
          <div class="dropdown-closed-icon icon"><img :src="dropdownClosed"></div>
        </a>
      </div>
      <div v-else class="spinner">
        <div class="custom-overlay">
          <spinner></spinner>
        </div>
      </div>
    </div>
    <input-validation-message :error="error" :message="message"
                              :submitted="submitted">
    </input-validation-message>
    <ul class="suggestion-list">
      <li class="top-suggestions" v-if="this.options.length">
        {{$t('general.topSuggestions')}}
      </li>
      <li :class="{'activeRow': index === highlightIndex}"
          @click="updateList(suggestion[1])"
          @mousedown.prevent
          @mouseover="hoverUpdate(index)"
          v-for="(suggestion, index) in matches">
        <div class="suggestion-label-container add-pointer">
          <div class="suggestion-label uk-text-truncate ellipsis-color">
            <a class="add-pointer suggestion">
              <img :class="{'empty-checkbox': !isChecked(suggestion[1])}" :src="activeCheckbox" class="checkbox"
                   v-if="multiSelection">
              <img :class="{'empty-checkbox': isChecked(suggestion[1])}" :src="emptyCheckBox" class="checkbox"
                   v-if="multiSelection">
              <input :value="suggestion[1].title" class="input-checkbox"
                     type="checkbox">
              {{suggestion[1].title}}
            </a>
          </div>
        </div>
      </li>
      <li class="no-suggestion-label" v-if="!matches.length>0"><a
        class="suggestion">{{$t('general.thereAreNoMatches')}}</a></li>
    </ul>
    <ul class="selected-list" v-if="multiSelection">
      <li :key="strandGUID(selected)" :title="strandDescription(selected)"
          class="add-pointer custom-text-truncate" uk-tooltip="pos: bottom-left" v-for="selected in selectedOptions">
        <label @click="updateList(selected)" class="option-label">
          <img :src="activeCheckbox"
               class="checkbox">
          {{ strandNumber(selected) }}
        </label>
      </li>
    </ul>
  </div>
</template>

<script>
  import spinner from '../../ui-components/spinner/spinner'
  import iconUtil from '../../../utils/icon-util'
  import * as misc from '../../../enums/misc'
  import inputValidationMessage from '../../ui-components/validation/input-validation-message'

  export default {
    name: 'strands-dropdown',
    props: ['options', 'placeholder', 'value', 'error', 'message', 'submitted', 'limit', 'selected', 'multiSelection', 'id', 'clearSelection', 'fetchComplete'],
    components: {
      spinner,
      inputValidationMessage
    },
    data() {
      return {
        searchText: '',
        selectedOptions: [],
        open: false,
        highlightIndex: 0,
        lastSearchText: '',
        dropdownOpen: iconUtil.resolve(misc.DROPDOWN_OPEN),
        closeIcon: iconUtil.resolve(misc.CLOSE_SLATE_14PX),
        dropdownClosed: iconUtil.resolve(misc.DROPDOWN_CLOSED),
        emptyCheckBox: iconUtil.resolve(misc.LRG_UNCHECKED),
        activeCheckbox: iconUtil.resolve(misc.LRG_CHECKED)
      }
    },
    computed: {
      matches() {
        if (this.options) {
          let matches = Object.entries(this.options).filter((option) => {
            let optionText = option[1].title.toUpperCase()
            return optionText.match(this.searchText.toUpperCase().replace(/\s+/g, '.+'))
          })
          return matches.length > 0 ? matches : {}
        }
      },
      resolvePlaceholder() {
        if (this.selectedOptions.length && !this.multiSelection) {
          return this.selectedOptions
        } else {
          return this.placeholder
        }
      },
      stylePlaceholder() {
        return (!this.multiSelection && this.selectedOptions.length)
      }
    },
    watch: {
      value: (newValue) => {
        this.updateComponentWithValue(newValue)
      },
      selected(newValue) {
        for (let item of newValue) {
          this.updateList(item)
        }
      },
      clearSelection(value) {
        if (value) {
          this.selectedOptions = []
          this.searchText = ''
        }
      },
      fetchComplete(complete) {
        if (complete) {
          // dont show dropdown when user is editing a resource and has'nt entered a strand search term
          if (this.selected && !this.searchText) {
            this.open = false
          } else {
            this.open = true
            this.$refs.strandsSearch.focus()
          }
        } else if (!complete) {
          this.open = false
        }
      }
    },
    methods: {
      strandNumber(strand) {
        try {
          return this.selectedOptions.find(o => o.id === strand.id).standard
        } catch (err) {
        }
      },
      strandDescription(strand) {
        try {
          return this.selectedOptions.find(o => o.id === strand.id).description
        } catch (err) {
        }
      },
      strandGUID(strand) {
        try {
          return this.selectedOptions.find(o => o.id === strand.id).id
        } catch (err) {
        }
      },
      handleFocusOut() {
        if (!this.multiSelection) {
          this.clear()
        }
      },
      isChecked(selectedOption) {
        return this.selectedOptions.filter(o => o.id === selectedOption.id).length > 0
      },
      setOpen(isOpen) {
        this.open = isOpen
        if (this.open) {
          this.$refs.strandsSearch.focus()
        }
      },
      searchChanged() {
        this.$emit('strandSearch', this.searchText)
      },
      defaultSearch() {
        this.$emit('defaultStrandSearch', 1)
      },
      updateComponentWithValue(newValue) {
        if (this.options) {
          if (Object.values(this.options).indexOf(newValue) > -1) {
            // Find the matching text for the supplied option value
            for (let text in this.options) {
              if (this.options.hasOwnProperty(text)) {
                if (this.options[text] === newValue) {
                  this.searchText = text
                }
              }
            }
          }
        }
      },
      up() {
        if (this.open) {
          if (this.highlightIndex > 0) {
            this.highlightIndex--
          }
        } else {
          this.setOpen(true)
        }
      },
      down() {
        if (this.open) {
          this.highlightIndex++
        } else {
          this.setOpen(true)
        }
      },
      removeSelectedOption(selectedOption) {
        let arr = this.selectedOptions
        this.selectedOptions = arr.filter((item) => {
          return item !== selectedOption
        })
      },
      removeStrandOption(selectedOption) {
        let arr = this.selectedOptions
        this.selectedOptions = arr.filter(item => item.id !== selectedOption.id)
      },
      updateList(selectedOption) {
        let guid = ''
        guid = selectedOption.id
        if (this.multiSelection) {
          if (this.selectedOptions.length < this.limit) {
            if (this.selectedOptions.find(o => o.id === guid)) {
              this.removeStrandOption(selectedOption)
            } else {
              if (this.options.length) {
                this.selectedOptions.push(this.options.find(o => o.id === guid))
              } else {
                this.selectedOptions.push(selectedOption)
              }
            }
          } else {
            if (this.selectedOptions.includes(selectedOption)) {
              this.removeSelectedOption(selectedOption)
            }
          }
        } else {
          this.selectedOptions = []
          this.selectedOptions.push(selectedOption)
          this.searchText = ''
          this.setOpen(false)
        }

        let strands = []
        for (let strand of this.selectedOptions) {
          strands.push(strand.id)
        }
        this.$emit('strandSelected', strands)
      },
      hoverUpdate(index) {
        this.highlightIndex = index
      },
      clear() {
        this.lastSearchText = this.searchText
        this.searchText = ''
      },
      checkOptions() {
        if (this.options) {
          for (let item of this.options) {
            if (item.selected) {
              this.selectedOptions.push(item)
            }
          }
        }
      }
    },
    mounted() {
      this.updateComponentWithValue(this.value)
      this.defaultSearch()
      this.checkOptions()
    },
    created() {
      if (this.selected) {
        for (let item of this.selected) {
          this.updateList(item)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../../../styles/colours';
  @import '../../../styles/breakpoints';

  .spinner {
    height: 38px !important;
    width: 38px;
    .custom-overlay{
      top: 5%;
      left: 86.5%;
      position: absolute;
    }
  }

  .dropdown {
    width: 100%;
    display: inline-block;
    position: relative;
  }

  .input-container {
    width: 100%;
    position: relative;
  }

  .close-icon {
    position: absolute;
    padding: 13px 0 0 13px;
  }

  .icon {
    width: 43px;
    height: 43px;
  }

  .custom-overlay {
    top: 30%;
    left: 91.5%;
    position: absolute;
  }

  .suggestion-list {
    background-color: $suggestion-list;
    border: $suggestion-list-border;
    opacity: 1;
    list-style: none;
    display: block;
    padding: 0;
    position: absolute;
    top: 45px;
    left: 0;
    z-index: 2;
    margin-top: 20px;
    margin-left: 0px !important;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 300px;
    box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.24);
    width: 100%;

    li {
      width: 100%;
    }
  }

  .toggle {
    position: absolute;
  }

  .dropdown.open .suggestion-list {
    display: block;
  }

  .dropdown .suggestion-list {
    display: none;
  }

  .toggle .uk-search-icon {
    display: none;
  }

  .open .toggle .uk-search-icon {
    display: inline-block;
  }

  .open .toggle .dropdown-closed-icon {
    display: none;
  }

  .suggestion-list li {
    cursor: pointer;
    padding-left: 9px;
  }

  .suggestion-list li:hover, .activeRow {
    color: #fff;
    background-color: $suggestion-list-hover;

    a.suggestion {
      color: $light-blue;
    }
  }

  .input-fields {
    width: 100%;
    border-radius: 1px;
    box-shadow: $input-shadow;
    background-color: white;
    font-size: 18px;
    color: $share-input-text;
    height: 46px;
    padding-left: 13px;

    &:hover, &:focus {
      border: 2px solid $light-blue;
      box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.24);
    }
  }

  .input-active {
    .input-fields {
      padding-left: 36px;
    }
  }

  .input-active {
    .input-selected {
      padding-left: 36px;
    }
  }

  .input-fields::placeholder {
    font-style: italic;
    font-size: 16px;
    color: $slate;
  }

  .input-fields:-ms-input-placeholder {
    font-style: italic !important;
    font-size: 16px !important;
    color: $slate !important;
  }

  .input-fields::-ms-input-placeholder {
    font-style: italic;
    font-size: 16px;
    color: $slate;
  }

  .input-selected {
    width: 100%;
    border-radius: 1px;
    box-shadow: $input-shadow;
    background-color: white;
    font-size: 18px;
    color: $share-input-text;
    height: 46px;
    padding-left: 13px;

    &:hover, &:focus {
      border: 2px solid $light-blue;
      box-shadow: 3px 5px 8px 0 rgba(0, 0, 0, 0.24);
    }
  }

  .input-selected::placeholder {
    font-size: 16px;
    color: $content-text
  }

  .input-selected:-ms-input-placeholder {
    font-size: 16px !important;
    color: $content-text !important;
  }

  .input-selected::-ms-input-placeholder {
    font-size: 16px;
    color: $content-text
  }

  .active {
    opacity: 0.8;
    background-color: $app-background;
  }

  .option-label {
    font-size: 15px;
    margin: 0px 0px 0px 3px;
  }

  li {
    width: 100%;
    height: 37px;
    list-style-type: none;
  }

  .no-suggestion-label {
    padding: 12px 0px 11px 26px !important;
    font-size: 15px;
    color: #7a8b99;
    height: 15px !important;
  }

  ul {
    padding: 12px 0px 0px 0px;
    margin: 0 !important;
  }

  .suggestion-label {
    font-size: 15px;
    padding: 10px 0px 0px 0px !important;
  }

  .suggestions-label-container {
    height: 37px;
  }

  .input-checkbox {
    display: none;
  }

  .empty-checkbox {
    display: none;
  }

  .checkbox {
    height: 22px;
    width: 22px;
  }

  a.suggestion {
    text-decoration: none;
    color: $content-text;
  }

  a:hover {
    font-weight: normal;
    color: $content-text;
    text-decoration: none;
  }

  .uk-form-danger {
    color: $input-warning;
    border: 1px solid $input-warning !important;
  }

  .ellipsis-color {
    &:hover {
      color: $light-blue;
    }

    color: black;
  }

  .uk-text-truncate {
    max-width: 95%;
  }

  .custom-text-truncate {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  li.top-suggestions {
    font-size: 18px;
    font-weight: bold;
    background: white;
    margin-top: 10px;

    &:hover {
      color: $content-text;
      background: white;
    }
  }

  @media screen and (max-width: $breakpoint-large) {
    .custom-overlay {
      right: 10.5%;
    }

    .spinner {
      .custom-overlay{
        top: 5%;
        left: 80%;
        position: absolute;
      }
    }
  }



</style>
